import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Share2, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { Modal } from "@/components/ui-bits";
import { themeOf, logoUrl, accentOf, onAccent, isHex } from "@/lib/brand";

const W = 1080, H = 1920;

const loadImg = (src) => new Promise((res) => { if (!src) return res(null); const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });

const ring = (ctx, x, y, r, pct, color, track) => {
  ctx.lineWidth = 26; ctx.lineCap = "round";
  ctx.strokeStyle = track; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
  if (pct > 0) { ctx.strokeStyle = color; ctx.beginPath(); ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct); ctx.stroke(); }
};

export async function renderStory(canvas, data) {
  const { brand, name, program, checkin, badges, weekDone, weekTotal, weekStart } = data;
  const t = themeOf(brand?.theme);
  const v = { ...t.vars, "--accent": accentOf(brand), "--on-accent": isHex(brand?.accent) ? onAccent(brand.accent) : t.vars["--on-accent"] };
  const streak = data?.shareStreak || 0;
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.fillStyle = v["--bg"]; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = v["--accent"]; ctx.fillRect(0, 0, W, 18);
  for (let i = 0; i < 1400; i++) { ctx.fillStyle = `rgba(128,128,128,${Math.random() * 0.06})`; ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2); }
  const logo = await loadImg(logoUrl(brand?.logoKey));
  if (logo) { ctx.save(); ctx.beginPath(); ctx.roundRect(90, 110, 120, 120, 28); ctx.clip(); ctx.drawImage(logo, 90, 110, 120, 120); ctx.restore(); }
  else { ctx.fillStyle = v["--ink"]; ctx.beginPath(); ctx.roundRect(90, 110, 120, 120, 28); ctx.fill(); ctx.fillStyle = v["--bg"]; ctx.font = "800 54px Helvetica, Arial"; ctx.fillText((brand?.studioName || "IP").slice(0, 2).toUpperCase(), 108, 190); }
  ctx.fillStyle = v["--ink"]; ctx.font = "800 48px Helvetica, Arial"; ctx.fillText((brand?.studioName || "MY COACH").toUpperCase(), 240, 165);
  ctx.fillStyle = v["--ink3"]; ctx.font = "500 30px Helvetica, Arial"; ctx.fillText(`Week of ${new Date(weekStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${name}`, 240, 212);
  ctx.fillStyle = v["--ink"]; ctx.font = "800 96px Helvetica, Arial"; ctx.fillText("MY WEEK", 90, 400);
  ctx.fillStyle = v["--accent"]; ctx.fillRect(90, 430, 180, 10);
  const pct = program?.total ? program.done / program.total : 0;
  ring(ctx, 540, 760, 230, pct, v["--accent"], v["--muted"]);
  ctx.textAlign = "center"; ctx.fillStyle = v["--ink"]; ctx.font = "800 150px Helvetica, Arial"; ctx.fillText(`${Math.round(pct * 100)}%`, 540, 800);
  ctx.fillStyle = v["--ink3"]; ctx.font = "500 34px Helvetica, Arial"; ctx.fillText(program ? `${program.done}/${program.total} exercises ticked` : "no plan this week", 540, 860);
  ctx.textAlign = "left";
  const tiles = [["SESSIONS", `${weekDone}/${weekTotal}`, "done this week"],
    ["MOOD", checkin ? `${checkin.mood}/5` : "—", "how I felt"], ["SLEEP", checkin ? `${checkin.sleep}/5` : "—", "how I rested"], ["SORENESS", checkin ? `${checkin.soreness}/5` : "—", "how I recovered"]];
  tiles.forEach(([l, val, sub], i) => {
    const x = 90 + (i % 2) * 460, y = 1080 + Math.floor(i / 2) * 230;
    ctx.fillStyle = v["--surface"]; ctx.beginPath(); ctx.roundRect(x, y, 440, 200, 28); ctx.fill();
    ctx.strokeStyle = v["--line"]; ctx.lineWidth = 3; ctx.stroke();
    ctx.fillStyle = v["--ink3"]; ctx.font = "700 26px Helvetica, Arial"; ctx.fillText(l, x + 32, y + 56);
    ctx.fillStyle = v["--ink"]; ctx.font = "800 76px Helvetica, Arial"; ctx.fillText(val, x + 32, y + 136);
    ctx.fillStyle = v["--ink3"]; ctx.font = "500 24px Helvetica, Arial"; ctx.fillText(sub, x + 32, y + 176);
  });
  let bx = 90, by = 1600;
  ctx.fillStyle = v["--ink3"]; ctx.font = "700 26px Helvetica, Arial"; ctx.fillText("BADGES", 90, by - 30);
  (badges?.length ? badges : [{ label: "Showing up" }]).slice(0, 4).forEach((b) => {
    ctx.font = "700 30px Helvetica, Arial"; const w = ctx.measureText(b.label).width + 64;
    if (bx + w > W - 90) { bx = 90; by += 84; }
    ctx.fillStyle = v["--accent"]; ctx.beginPath(); ctx.roundRect(bx, by, w, 64, 32); ctx.fill();
    ctx.fillStyle = v["--on-accent"]; ctx.fillText(b.label, bx + 32, by + 43); bx += w + 16;
  });
  ctx.fillStyle = v["--ink3"]; ctx.font = "500 26px Helvetica, Arial"; ctx.fillText(`${brand?.tagline || t.tagline}  ·  Powered by SpottrOS`, 90, 1850);
  if (streak >= 4) {
    const gold = ctx.createLinearGradient(0, 0, W, H); gold.addColorStop(0, "#f7d774"); gold.addColorStop(.5, "#c99a2e"); gold.addColorStop(1, "#f1c75a");
    ctx.strokeStyle = gold; ctx.lineWidth = 22; ctx.strokeRect(11, 11, W - 22, H - 22);
    ctx.lineWidth = 3; ctx.strokeRect(40, 40, W - 80, H - 80);
    ctx.save(); ctx.translate(W - 150, 150); ctx.rotate(Math.PI / 4);
    ctx.fillStyle = gold; ctx.fillRect(-260, -34, 520, 68);
    ctx.fillStyle = "#2a1d05"; ctx.font = "800 30px Helvetica, Arial"; ctx.textAlign = "center"; ctx.fillText(`${streak}-WEEK STREAK`, 0, 11); ctx.restore(); ctx.textAlign = "left";
  }
}

export const ShareWeek = ({ brand, data }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [streak, setStreak] = useState(null);
  const ref = useRef(null);
  useEffect(() => { api.get("/client/me/share").then((r) => setStreak(r.data)); }, []);
  const recordShare = async (method) => { try { const r = await api.post("/client/me/share", { method }); const was = streak; setStreak(r.data); if (!was?.sharedThisWeek) toast.success(r.data.streak > 1 ? `Shared — ${r.data.streak}-week share streak!` : "Shared — streak started. Share again next week to keep it going."); } catch { /* ignore */ } };
  useEffect(() => {
    if (!open) return;
    const c = document.createElement("canvas");
    renderStory(c, { ...renderData, brand }).then(() => { setUrl(c.toDataURL("image/png")); ref.current = c; });
  }, [open, data, brand, streak]); // eslint-disable-line react-hooks/exhaustive-deps
  const file = () => new Promise((res) => ref.current.toBlob((b) => res(new File([b], "my-week.png", { type: "image/png" })), "image/png"));
  const download = () => { const a = document.createElement("a"); a.href = url; a.download = "my-week.png"; a.click(); recordShare("download"); };
  const share = async () => {
    const f = await file();
    if (navigator.canShare?.({ files: [f] })) { try { await navigator.share({ files: [f], title: "My week" }); recordShare("share"); } catch { /* cancelled */ } }
    else { download(); toast.success("Saved — add it to your story from your camera roll"); }
  };
  const atRisk = streak?.streak > 0 && !streak.sharedThisWeek;
  const renderData = { ...data, shareStreak: streak?.streak || 0 };
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {streak?.streak > 0 && <span className={`chip ${streak.streak >= 4 ? "gold" : "gray"}`} data-testid="share-streak-chip"><Flame size={12} style={{ color: streak.streak >= 4 ? "#c99a2e" : "var(--accent)" }} />{streak.streak}-week share streak{streak.streak >= 4 ? " · Sharer badge" : ""}{atRisk ? " · share to keep it" : " ✓"}</span>}
        <button className="btn sm g" onClick={() => setOpen(true)} data-testid="share-week-button"><Share2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Share my week</button>
      </div>
      {open && (
        <Modal title="SHARE MY WEEK" sub="Story-sized (1080×1920) · branded · ready for Instagram" onClose={() => setOpen(false)} testId="share-week-modal">
          <div style={{ display: "flex", justifyContent: "center", background: "var(--muted)", borderRadius: 12, padding: 12 }}>
            {url ? <img src={url} alt="My week" style={{ height: 440, borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,.25)" }} data-testid="share-week-preview" /> : <span className="spin" />}
          </div>
          {streak && <p className="ss" style={{ marginTop: 10 }} data-testid="share-streak-note">{streak.streak >= 4 ? `Gold frame unlocked — ${streak.streak}-week share streak. ` : ""}{atRisk ? `Share now to keep your ${streak.streak}-week streak alive.` : streak.sharedThisWeek ? `Shared this week · ${streak.streak}-week streak.` : `Share every week to build a streak${streak.streak < 4 ? " — the gold frame unlocks at 4 weeks" : ""}.`}</p>}
          <div className="mfoot">
            <button className="btn g" onClick={download} disabled={!url} data-testid="share-week-download">Download</button>
            <button className="btn" onClick={share} disabled={!url} data-testid="share-week-share">Share</button>
          </div>
        </Modal>
      )}
    </>
  );
};
